import { useState, useContext, useEffect, useMemo, useRef, createContext } from 'react'
import { TransitionGroup } from 'react-transition-group'
import { HexColorPicker } from "react-colorful"

import { flatten } from "flat"
import ShortUniqueId from 'short-unique-id'
import _ from "lodash"
import moment from 'moment-timezone'
import isEmail from 'validator/lib/isEmail'
import isURL from 'validator/lib/isURL'

import { Card, CardHeader, Slide, List, ListItem, Collapse, TextField, Typography, IconButton, Button, FormControlLabel, RadioGroup, Radio, Switch, Stack, Tooltip, CardContent, Paper, InputAdornment, MenuItem } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import WestRoundedIcon from '@mui/icons-material/WestRounded'
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined'
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import NotificationAddIcon from '@mui/icons-material/NotificationAdd'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'

import { AppContext } from '/providers/app-context'
import { PassContext } from '../pass-context'
import dataSelector from './data-selector'
import VariablesSelector from '/utils/variable-selector'
import getFormattedValue from './popup-editor-field-formatter'
import findPathByValue from '/utils/find-path-by-value'
import currencies from '/utils/currencies.json'
import PopupShell from '../popup-shell'

const uuid = new ShortUniqueId({ length: 16 })
const shortUUID = () => uuid.rnd()
const EditorContext = createContext({})

const isIntegerInput = input => /^-?\d*$/.test(input)
const isDecimalInput = input => /^-?\d*(\.\d*)?$/.test(input)
const isValidCurrencyCodeInput = input => /^[A-Z]{3}$/.test(input ?? "")
  && currencies.some(currency => currency.code === input)
const isValidImageUrlInput = input => {
  if (!isURL(input, { protocols: ['http', 'https'], require_protocol: true })) return false

  try {
    const { pathname } = new URL(input)
    return /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(pathname)
  }
  catch {
    return false
  }
}
const isValidColourInput = input => /^#?[0-9a-fA-F]{6}$/.test(input)
const normaliseColourInput = input => /^#[0-9a-fA-F]{6}$/.test(input)
  ? input
  : /^[0-9a-fA-F]{6}$/.test(input)
    ? `#${input}`
    : null
const isValidDateInput = input => /^\d{8}$/.test(input) && moment(input, "YYYYMMDD", true).isValid()
const isValidDateTimeInput = input => /^\d{14}$/.test(input) && moment(input, "YYYYMMDDHHmmss", true).isValid()
const isValidLocationInput = input => {
  if (typeof input !== "string") return false

  const parts = input.split(",").map(part => part.trim())
  if (parts.length !== 2 || parts.some(part => part === "")) return false

  const [latitude, longitude] = parts.map(Number)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false

  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
}

const validateValueInput = ({ input, varSubType }) => {
  const value = input ?? ""
  if (value === "") return { valid: true, helperText: "" }

  switch (varSubType) {
    case "email":
      return { valid: isEmail(value), helperText: isEmail(value) ? "" : "Enter a valid email address." }
    case "link":
      return {
        valid: isURL(value, { protocols: ['http', 'https'], require_protocol: true }),
        helperText: isURL(value, { protocols: ['http', 'https'], require_protocol: true }) ? "" : "Enter a valid URL starting with http:// or https://."
      }
    case "image":
      return {
        valid: isValidImageUrlInput(value),
        helperText: isValidImageUrlInput(value) ? "" : "Enter a valid http:// or https:// image URL ending in an image file extension."
      }
    case "number":
    case "money":
      return { valid: isDecimalInput(value), helperText: isDecimalInput(value) ? "" : "Enter a valid number." }
    case "date":
      return { valid: isValidDateInput(value), helperText: isValidDateInput(value) ? "" : "Use YYYYMMDD and enter a valid date." }
    case "datetime":
      return {
        valid: isValidDateTimeInput(value),
        helperText: isValidDateTimeInput(value) ? "" : "Use YYYYMMDDHHmmss and a valid 24-hour time."
      }
    case "location":
      return {
        valid: isValidLocationInput(value),
        helperText: isValidLocationInput(value) ? "" : "Use latitude,longitude with valid ranges."
      }
    case "colour":
      return {
        valid: isValidColourInput(value),
        helperText: isValidColourInput(value) ? "" : "Use a 6-digit hex colour such as #A1B2C3."
      }
    case "choice":
      return {
        valid: isValidCurrencyCodeInput(value),
        helperText: isValidCurrencyCodeInput(value) ? "" : "Use a valid 3-letter ISO currency code such as GBP."
      }
    default:
      return { valid: true, helperText: "" }
  }
}

const toNativeDateValue = value => value && isValidDateInput(value) ? moment(value, "YYYYMMDD").format("YYYY-MM-DD") : ""
const toNativeDateTimeValue = value => value && isValidDateTimeInput(value) ? moment(value, "YYYYMMDDHHmmss").format("YYYY-MM-DDTHH:mm:ss") : ""
const fromNativeDateValue = value => value ? moment(value, "YYYY-MM-DD").format("YYYYMMDD") : ""
const fromNativeDateTimeValue = value => value ? moment(value, "YYYY-MM-DDTHH:mm:ss").format("YYYYMMDDHHmmss") : ""

const applyFormattingDefaults = (fieldItem, value) => {
  fieldItem.type = value
  delete fieldItem.ignoresTimeZone
  delete fieldItem.isRelative
  delete fieldItem.dateStyle
  delete fieldItem.timeStyle
  delete fieldItem.numberStyle
  delete fieldItem.currencyCode

  switch (value) {
    case "number":
      if (!fieldItem.numberStyle) fieldItem.numberStyle = "PKNumberStyleDecimal"
      break

    case "date":
      if (!fieldItem.dateStyle) fieldItem.dateStyle = "PKDateStyleShort"
      if (fieldItem.isRelative === undefined) fieldItem.isRelative = false
      if (fieldItem.ignoresTimeZone === undefined) fieldItem.ignoresTimeZone = true
      break

    case "time":
      if (!fieldItem.timeStyle) fieldItem.timeStyle = "PKDateStyleShort"
      if (fieldItem.ignoresTimeZone === undefined) fieldItem.ignoresTimeZone = true
      break

    case "datetime":
      if (!fieldItem.dateStyle) fieldItem.dateStyle = "PKDateStyleShort"
      if (!fieldItem.timeStyle) fieldItem.timeStyle = "PKDateStyleShort"
      if (fieldItem.isRelative === undefined) fieldItem.isRelative = false
      if (fieldItem.ignoresTimeZone === undefined) fieldItem.ignoresTimeZone = true
      break

    case "money":
      if (!fieldItem.currencyCode) fieldItem.currencyCode = "GBP"
      break
  }
}

const applyFormattingPresetFromCatalog = (fieldItem, catalogEntry) => {
  const formatting = catalogEntry?.var_formatting ?? ({
    integer: "number",
    number: "number",
    money: "money",
    date: "date",
    time: "time",
    datetime: "datetime",
    text: "text",
    www: "www",
    phone: "phone",
    email: "email"
  }[catalogEntry?.var_type] ?? null)
  const formattingSubType = catalogEntry?.var_formatting_sub_type ?? catalogEntry?.var_sub_type ?? catalogEntry?.sub__type

  if (!formatting) return

  applyFormattingDefaults(fieldItem, formatting)

  switch (formatting) {
    case "number":
      if (formattingSubType) fieldItem.numberStyle = formattingSubType
      break

    case "date":
      if (formattingSubType) fieldItem.dateStyle = formattingSubType
      break

    case "time":
      if (formattingSubType) fieldItem.timeStyle = formattingSubType
      break

    case "datetime":
      if (formattingSubType) {
        fieldItem.dateStyle = formattingSubType
        fieldItem.timeStyle = formattingSubType
      }
      break

    case "money":
      if (formattingSubType) fieldItem.currencyCode = formattingSubType
      break
  }
}

const getCatalogPathFromReference = value => {
  if (typeof value !== "string") return ""

  const match = value.match(/^{{([^}]+)}}$/)
  return match?.[1] ?? ""
}

const PopupDialogFields = ({ open, close, title, path, row }) => {

  const classReferences = {
    frontFields: `classTemplateInfo.cardTemplateOverride.cardRowTemplateInfos[${row}]`,
    backFields: `classTemplateInfo.detailsTemplateOverride.detailsItemInfos`
  }

  // Path to our data row in class based on supplied path (row number)
  const classRowReference = classReferences[path]

  const { client } = useContext(AppContext)
  const { classData, setClassData, objectData, setObjectData, variables, setVariables, setGoogleBoth, openVariablesManager, variablesCatalog = [], isManagedPassTemplate = false } = useContext(PassContext)

  const [fields, setFields] = useState([])
  const [originalFields, setOriginalFields] = useState([])
  const [newVariables, setNewVariables] = useState({})
  const [validationErrors, setValidationErrors] = useState({})
  const [sourcePaths, setSourcePaths] = useState([])
  const [view, setView] = useState("list")
  const catalogMap = useMemo(() => _.keyBy(variablesCatalog, "var_name"), [variablesCatalog])
  const hasValidationErrors = useMemo(() => Object.values(validationErrors).some(Boolean), [validationErrors])

  const maxFields = path === "frontFields" ? 3 : 11 // Always 3 for Google front fields. back is more complex. You can have 10 (documented, but allows 11) from object and 10 from class...(but we are only using object)

  const dataChanged = useMemo(() => {
    return !_.isEqual(fields, originalFields) || !_.isEqual(newVariables, variables)
  }, [fields, originalFields, newVariables, variables])

  const getClassItemData = rowRef => {
    const cdSplit = rowRef.split(".") // object or class
    const rowRefPrefix = cdSplit[0] === "object" ? objectData : classData
    const rowRefSuffix = rowRef.replace(`${cdSplit[0]}.`, "") // The rest of the "rowRef" string with object/class removed

    const path = rowRefSuffix.replace(/(.*)\[.*/, "$1") // Grab the part before the bracket (textModulesData or linkModuleData.uris)
    const id = rowRefSuffix.replace(/.*\['(.*)'\].*/, "$1")
    const pf = _.get(rowRefPrefix, path)

    let data = pf.filter(i => i.id === id)[0]

    if (data.description) { // Make URIs look like text data. Easier to handle. We'll rebuild when we save
      data.header = data.description
      data.body = data.uri

      delete data.description
      delete data.uri
    }

    return {
      ...data,
      type: data.type ?? "text"
    }
  }

  useEffect(() => {
    if (open) {
      const row = _.cloneDeep(_.get(classData, classRowReference))
      const rowType = Object.keys(row)[0]

      let initialFields = []

      switch (rowType) {
        case "0": { // Back fields
          row.map(r => {
            initialFields.push({field:getClassItemData(r.item.firstValue.fields[0].fieldPath),readonly:r.item.readonly ?? {header:false,body:false}})
          })
          break
        }

        case "oneItem": { // Front fields
          initialFields.push({field:getClassItemData(row.oneItem.item.firstValue.fields[0].fieldPath),readonly:row.oneItem.item.readonly ?? {header:false,body:false}})
          break
        }
        case "twoItems": { // Front fields
          initialFields.push({field:getClassItemData(row.twoItems.startItem.firstValue.fields[0].fieldPath),readonly:row.twoItems.startItem.readonly ?? {header:false,body:false}})
          initialFields.push({field:getClassItemData(row.twoItems.endItem.firstValue.fields[0].fieldPath),readonly:row.twoItems.endItem.readonly ?? {header:false,body:false}})
          break
        }
        case "threeItems": { // Front fields
          initialFields.push({field:getClassItemData(row.threeItems.startItem.firstValue.fields[0].fieldPath),readonly:row.threeItems.startItem.readonly ?? {header:false,body:false}})
          initialFields.push({field:getClassItemData(row.threeItems.middleItem.firstValue.fields[0].fieldPath),readonly:row.threeItems.middleItem.readonly ?? {header:false,body:false}})
          initialFields.push({field:getClassItemData(row.threeItems.endItem.firstValue.fields[0].fieldPath),readonly:row.threeItems.endItem.readonly ?? {header:false,body:false}})
          break
        }
      }

      setFields(initialFields)
      setOriginalFields(_.cloneDeep(initialFields))
      setNewVariables(_.cloneDeep(variables))
      setValidationErrors({})
      setView("list")
    }
  }, [open, path, classData, variables])

  // Refresh our template / instance data source paths if newVariables changes (via popup)
  // Will refresh on all changes, not just those from manage data popup - but that is not a problem.
  useEffect(() => {
    setSourcePaths(Object.keys(flatten(newVariables)).map(p => `{{${p}}}`).sort())
  }, [newVariables])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const addItem = () => {

    const newFieldId = shortUUID()

    const newField = {
      id: newFieldId,
      header: "",
      body: "",
      type: "text"
    }

    setFields((prev) => [...prev, {field:newField,readonly:{header:false,body:false}}])
  }

  const addNotifications = () => {
    const newField = {
      id: "smartix_notifications",
      header: "Notifications",
      body: "[Notifications will appear here]",
      type: "text"
    }

    setFields((prev) => [{field:newField,readonly:{header:false,body:true}}, ...prev])
  }

  const addAdvert = () => {
    const newField = {
      id: "smartix_advert",
      header: "Advert",
      body: "[Advert will appear here]",
      type: "text"
    }

    setFields((prev) => [...prev, {field:newField,readonly:{header:false,body:true}}])
  }



  const resolveValue = (item, field) => dataSelector({ objectData: item, variables: newVariables, path: field })

  const getFrontFieldSavePath = idx => {
    switch (fields.length) {
      case 1: return {
        field: "oneItem.item.firstValue.fields[0].fieldPath",
        readonly : "oneItem.item.readonly"
      }

      case 2: return {
        0: {
          field: "twoItems.startItem.firstValue.fields[0].fieldPath",
          readonly: "twoItems.startItem.readonly"
        },
        1: {
          field : "twoItems.endItem.firstValue.fields[0].fieldPath",
          readonly : "twoItems.endItem.readonly"
        }
      }[idx]

      case 3: return {
        0: {
          field : "threeItems.startItem.firstValue.fields[0].fieldPath",
          readonly : "threeItems.startItem.readonly"
        },
        1: {
          field :"threeItems.middleItem.firstValue.fields[0].fieldPath",
          readonly :"threeItems.middleItem.readonly"
        },
        2: {
          field :"threeItems.endItem.firstValue.fields[0].fieldPath",
          readonly :"threeItems.endItem.readonly"
        }
      }[idx]
    }
  }

  const typesLookup = {
    text: "textModulesData",
    number: "textModulesData",
    date: "textModulesData",
    time: "textModulesData",
    datetime: "textModulesData",
    money: "textModulesData",
    www: "linksModuleData.uris",
    email: "linksModuleData.uris",
    location: "linksModuleData.uris",
    phone: "linksModuleData.uris",
  }

  function deleteUnwantedTextAndLinksModules({ newClassData, newObjectData }) {
    try {
      let moduleData = {
        class: {
          text: {},
          link: {}
        },
        object: {
          text: {},
          link: {}
        }
      }

      function getModuleData(source,type,id) {
        if (source === "class") {
          if (type === "text") {
            return newClassData.textModulesData.filter(i => i.id === id)[0]
          } else { // link
            return newClassData.linksModuleData.uris.filter(i => i.id === id)[0]
          }
        } else { // object
          if (type === "text") {
            return newObjectData.textModulesData.filter(i => i.id === id)[0]
          } else { // link
            return newObjectData.linksModuleData.uris.filter(i => i.id === id)[0]
          }
        }
      }

      function pushId(fieldPath) {
        const source = fieldPath.split(".")[0]
        const type = /textModulesData/.test(fieldPath) ? "text" : "link"
        const id = fieldPath.split("'")[1]
        moduleData[source][type][id] = getModuleData(source,type,id)
      }

      // Get IDs for front field items
      newClassData.classTemplateInfo.cardTemplateOverride.cardRowTemplateInfos.map(i => {
        switch (Object.keys(i)[0]) {
          case "oneItem":
            pushId(i.oneItem.item.firstValue.fields[0].fieldPath)
            break

          case "twoItems":
            pushId(i.twoItems.startItem.firstValue.fields[0].fieldPath)
            pushId(i.twoItems.endItem.firstValue.fields[0].fieldPath)
            break

          case "threeItems":
            pushId(i.threeItems.startItem.firstValue.fields[0].fieldPath)
            pushId(i.threeItems.middleItem.firstValue.fields[0].fieldPath)
            pushId(i.threeItems.endItem.firstValue.fields[0].fieldPath)
            break
        }
      })

      // Get IDs for back field items
      newClassData.classTemplateInfo.detailsTemplateOverride.detailsItemInfos.map(i => {
        pushId(i.item.firstValue.fields[0].fieldPath)
      })

      // Now we have a list of all the required class.textModulesData / class.linkModuleData / object.textModulesData / object.linkModuleData IDs
      // So we can go through those and remove any items not in our list
      // We also remove duplicates...

      newClassData.textModulesData = []
      for (let id in moduleData.class.text) {
        newClassData.textModulesData.push(moduleData.class.text[id])
      }
      newClassData.linksModuleData.uris = []
      for (let id in moduleData.class.link) {
        newClassData.linksModuleData.uris.push(moduleData.class.link[id])
      }

      newObjectData.textModulesData = []
      for (let id in moduleData.object.text) {
        newObjectData.textModulesData.push(moduleData.object.text[id])
      }
      newObjectData.linksModuleData.uris = []
      for (let id in moduleData.object.link) {
        newObjectData.linksModuleData.uris.push(moduleData.object.link[id])
      }
    }
    catch (err) {
      console.log(err)
    }
  }

  function upsertItems(source, path, newItems) {
    let targetArray = _.get(source, path)

    // Build a map from id → index in target array for quick lookup
    const indexMap = targetArray.reduce((map, item, idx) => {
      map[item.id] = idx
      return map
    }, {})

    newItems.forEach(item => {
      if (indexMap.hasOwnProperty(item.id)) {
        // Update existing item
        targetArray[indexMap[item.id]] = item
      } else {
        // Insert new item at the end
        targetArray.push(item)
      }
    })
  }

  const saveFormData = () => {

    if (!_.isEqual(fields, originalFields)) {
      // When saving we need to overwrite the ClassData.classTemplateInfo
      let cardRowTemplateInfos = {}
      let detailsItemInfos = []

      // And also update textModulesData and linksModuleData.uris in both classData and objectData
      // These will hold the new values. We need to add these to classData / objectData, not replace what's there...
      const variables = {
        class: {
          textModulesData: [],
          "linksModuleData.uris": []
        },
        object: {
          textModulesData: [],
          "linksModuleData.uris": []
        }
      }

      // Create the new cardRowTemplateInfos data
      fields.map((f, idx) => {
        const savepath = path === "frontFields" ? getFrontFieldSavePath(idx) : {field:"item.firstValue.fields[0].fieldPath",readonly:"item.readonly"}

        const classOrObject = "object" //(/{{.*}}/.test(f.field.body + f.field.header)) ? "object" : "class" // If we are referencing instance data then it's an object, otherwise a class item
        const type = typesLookup[f.field.type] // textModule or linkModule
        const value = `${classOrObject}.${type}['${f.field.id}']`

        let item = { ...f.field }
        const readonly = f.readonly.header || f.readonly.body

        // Earlier, we made URIs look like text data. Easier to handle. 
        // Now we need to return them to the correct format
        if (type === "linksModuleData.uris") {
          item.description = item.header
          item.uri = item.body

          delete item.header
          delete item.body
        }

        // Push item to class/object and text/link module
        variables[classOrObject][type].push(item)

        if (path === "frontFields") {
          _.set(cardRowTemplateInfos, savepath.field, value)
          if (readonly) _.set(cardRowTemplateInfos,savepath.readonly,{header:f.readonly.header,body:f.readonly.body})
        } else {
          let item = {}
          _.set(item, savepath.field, value)
          if (readonly) _.set(item, savepath.readonly, {header:f.readonly.header,body:f.readonly.body})
          detailsItemInfos.push(item)
        }
      })

      // Now clone classData and objectData.
      let newClassData = _.cloneDeep(classData)
      let newObjectData = _.cloneDeep(objectData)

      // Update classData cardRowTemplateInfos / detailsItemInfos
      _.set(newClassData, classReferences[path], (path === "frontFields") ? cardRowTemplateInfos : detailsItemInfos)

        // Insert or update items into class/object textModulesData / linksModuleData.uris
        ;["object", "class"].map(objectOrClass => {
          ["textModulesData", "linksModuleData.uris"].map(path => {
            upsertItems(objectOrClass === "class" ? newClassData : newObjectData, path, variables[objectOrClass][path])
          })
        })

      deleteUnwantedTextAndLinksModules({ newClassData, newObjectData })

      // Compare current and original classData and objectData and save as required
      if (!_.isEqual(newClassData, classData) && !_.isEqual(newObjectData, objectData)) {
        setGoogleBoth({
          googleClass: newClassData,
          googleObject: newObjectData
        })
      } else {
        if (!_.isEqual(newClassData, classData)) setClassData(newClassData)
        if (!_.isEqual(newObjectData, objectData)) setObjectData(newObjectData)
      }
    }

    if (!_.isEqual(newVariables, variables)) setVariables(newVariables)

    close()
  }


  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick") return
    if (reason && reason === "escapeKeyDown") {
      if (view === "edit") {
        setView("list")
        return
      }
    }
    close()
  }



  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const limitedEditing = (item, field) => (field === "value" && client.client_id !== 0 && item.field.id.match(/__SMARTIX/) !== null)
  const showDeveloperBackFieldActions = client.client_id === 0 && path === "backFields"
  const hasNotificationsField = fields.some(field => field.field.id === "smartix_notifications")
  const hasAdvertField = fields.some(field => field.field.id === "smartix_advert")

  return (
    <PopupShell open={open} onClose={handleClose}>
      <div className="flex flex-col p-2 h-full w-full overflow-x-hidden">

        <div className="flex flex-row justify-between mt-5 w-full">
          <div className="flex flex-row items-center gap-1">
            <IconButton color='primary' onClick={addItem} disabled={fields?.length >= maxFields || view !== "list"}><AddCircleOutlineOutlinedIcon /></IconButton>
            {showDeveloperBackFieldActions && <IconButton color='primary' onClick={addNotifications} disabled={fields?.length >= maxFields || view !== "list" || hasNotificationsField}><NotificationAddIcon /></IconButton>}
            {showDeveloperBackFieldActions && <IconButton color='primary' onClick={addAdvert} disabled={fields?.length >= maxFields || view !== "list" || hasAdvertField}><AddBusinessIcon /></IconButton>}
          </div>
          <Typography color="secondary" variant="h6">{title}</Typography>
          <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
        </div>

        <hr />

        <EditorContext.Provider value={{ resolveValue, limitedEditing, fields, setFields, sourcePaths, variables: newVariables, setVariables: setNewVariables, dataSelector, path, catalogMap, validationErrors, setValidationErrors, isManagedPassTemplate }}>
          <CardsContainer view={view} setView={setView} path={path} />
        </EditorContext.Provider>

        <div className='fixed bottom-5 w-[460px]'>

          <hr className='mb-5' />

          <div className="flex flex-row w-full justify-between">
            <Button startIcon={<DatasetOutlinedIcon />} onClick={openVariablesManager} >Manage variables</Button>
            <Button variant="contained" onClick={saveFormData} disabled={!dataChanged || hasValidationErrors}>Save</Button>
          </div>
        </div>
      </div>
    </PopupShell>
  )
}

export default PopupDialogFields


//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passed as parameters and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////

const CardsContainer = ({ view, setView }) => {

  const { fields } = useContext(EditorContext)
  const [selectedItem, setSelectedItem] = useState()
  const containerRef = useRef(null)

  useEffect(() => {
    if (selectedItem) {
      const item = fields.filter(f => f.field.id === selectedItem.field.id)[0]
      if (item) setSelectedItem(item)
    }
  }, [fields])

  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 })
  }, [view, selectedItem?.field?.id])

  return (
    <div ref={containerRef} className='mt-5 mb-[100px] relative w-full h-full overflow-y-scroll overflow-x-hidden'>
      <CardList view={view} setView={setView} setSelectedItem={setSelectedItem} />
      <EditCard selectedItem={selectedItem} view={view} setView={setView} />
    </div>
  )
}


const CardList = ({ view, setView, setSelectedItem }) => {

  const endRef = useRef(null)
  const { fields } = useContext(EditorContext)

  // track previous length
  const prevLengthRef = useRef(fields.length)

  useEffect(() => {
    // update refs if items removed
    if (fields.length < prevLengthRef.current) {
      prevLengthRef.current = fields.length
    }
  }, [fields])


  return (

    <Slide
      direction="right"
      in={view === "list"}
      timeout={400}
      mountOnEnter
      unmountOnExit
    >
      <div className='absolute top-0 left-0 w-full'>
        <List disablePadding dense >
          <TransitionGroup>
            {fields.map((item, idx) => (
              <Collapse key={item.field.id}
                onEntered={() => {
                  // only scroll if a new item was added
                  if (
                    idx === fields.length - 1 &&
                    fields.length > prevLengthRef.current
                  ) {
                    endRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "end",
                    })
                  }
                  prevLengthRef.current = fields.length
                }}>
                <ItemCard
                  idx={idx}
                  item={item}
                  setView={setView}
                  value={item.field.value}
                  setSelectedItem={setSelectedItem}
                />
              </Collapse>
            ))}
          </TransitionGroup>
        </List>
        <div ref={endRef} />
      </div>
    </Slide>
  )
}



const ItemCard = ({ item, idx, setView, setSelectedItem }) => {

  const { resolveValue, fields, setFields } = useContext(EditorContext)

  const resolvedValue = resolveValue(item.field, "body") ?? ""
  const resolvedLabel = resolveValue(item.field, "header") ?? ""

  let title = resolvedLabel.length ? resolvedLabel : "[label not set]"
  let subtitle = getFormattedValue({ item:item.field, resolvedValue: resolvedValue?.toString().length ? resolvedValue : "[value not set]" })

  const edit = () => {
    setSelectedItem(item)
    setView("edit")
  }

  const deleteItem = item => {
    setFields(prev => [...prev.filter((i) => i !== item)])
  }

  const moveItem = (item, direction) => {

    let fromIndex

    setFields((prev) => {
      fromIndex = _.indexOf(prev, item)
      return prev.filter((i) => i !== item)
    })

    setTimeout(() =>
      setFields((prev) => {
        const toIndex = fromIndex + (direction === "up" ? -1 : 1)
        return [
          ...prev.slice(0, toIndex),
          item,
          ...prev.slice(toIndex),
        ]
      }),
      300)
  }

  return (
    <ListItem disablePadding={true}>
      <Card variant="outlined" sx={{
        minHeight: "min-content",
        width: "448px",
        marginLeft: "-10px",
        position: "relative",
        overflow: "hidden",
        marginBottom: 1,
        bgcolor: 'rgba(0,0,0,0.1)'
      }}>
        <FieldPositionGraphic idx={idx} />
        <CardHeader
          title={title}
          subheader={subtitle}
          slotProps={{
            title: {
              sx: { color: resolvedValue.length ? "lightgray" : "darkgray", fontSize: "14px" }
            },
            subheader: {
              sx: { color: resolvedValue.length ? "secondary.main" : "darkgray", fontSize: "20px" }
            }
          }}
          style={{ cursor: "pointer" }}
          onClick={edit}
        />

        <div className='flex flex-row justify-between w-full'>
          <div><IconButton sx={{ color: "red" }} onClick={() => deleteItem(item)} disabled={item.readonly.header || item.readonly.body}><DeleteForeverOutlinedIcon /></IconButton></div>
          <div>
            <IconButton disabled={idx === 0} onClick={() => moveItem(item, "up")}><ArrowCircleUpOutlinedIcon /></IconButton>
            <IconButton disabled={idx === (fields.length - 1)} onClick={() => moveItem(item, "down")}><ArrowCircleDownOutlinedIcon /></IconButton>
          </div>
          <div><IconButton color="primary" onClick={edit}><SettingsOutlinedIcon /></IconButton></div>
        </div>
      </Card>
    </ListItem>
  )
}

const FieldPositionGraphic = ({ idx }) => {

  const { fields, path } = useContext(EditorContext)
  if (path !== "backFields") {
    const items = fields.map((d, item) => <div key={item} className={"item" + (item === idx ? " selected" : "")}></div>)
    return <div className='field-position-graphic'>{items}</div>
  }
}



const EditCard = ({ selectedItem, view, setView }) => {
  const { client } = useContext(AppContext)
  const canEditReadonly = Boolean(client?.is_smartix_account)

  const back = () => setView("list")

  if (selectedItem) return (
    <Slide
      direction="left"
      in={view === "edit"}
      timeout={400}
      mountOnEnter
      unmountOnExit
    >
      <div className='absolute top-0 left-0 w-full'>
        <Paper sx={{ bgcolor: 'background.paper', p: 1, position: "sticky", top: 0, left: 0, width: "100%", zIndex: 50 }} elevation={16}>
          <div className='flex flex-row justify-between'>
            <Button startIcon={<WestRoundedIcon />} onClick={back} >back</Button>
          </div>
        </Paper>

        <div className='overflow-y-scroll'>
          <List>
            <EditCardValueAndLabel item={selectedItem} field="header" />
            <EditCardValueAndLabel item={selectedItem} field="body" />
            {canEditReadonly && <ReadonlySettings item={selectedItem} />}
            <FieldFormatting item={selectedItem} />
            <FieldFormattingSubTypeWrapper item={selectedItem} />
            <DateTimeOptions item={selectedItem} />
            <ChangeMessage item={selectedItem} />
          </List>
        </div>
      </div>
    </Slide>
  )
}

const ReadonlySettings = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateReadonly = (field, value) => {
    setFields(prev => prev.map(f => {
      if (f.field.id !== item.field.id) return f
      return {
        ...f,
        readonly: {
          header: f.readonly?.header ?? false,
          body: f.readonly?.body ?? false,
          [field]: value
        }
      }
    }))
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <CardContent>
        <Typography sx={{ color: "secondary.main", marginBottom: 1 }}>Read Only</Typography>
        <Stack spacing={1}>
          <FormControlLabel
            control={<Switch checked={Boolean(item.readonly?.header)} onChange={e => updateReadonly("header", e.target.checked)} />}
            label="Label is read only"
          />
          <FormControlLabel
            control={<Switch checked={Boolean(item.readonly?.body)} onChange={e => updateReadonly("body", e.target.checked)} />}
            label="Value is read only"
          />
        </Stack>
      </CardContent>
    </Card>
  )
}


const field_lookup = {
  header : "label",
  body : "value"
}
const EditCardValueAndLabel = ({ item, field }) => {
  const { setFields, variables, dataSelector, catalogMap } = useContext(EditorContext)

  const initialMode = item.field[field]?.toString().match(/{{.*}}/) ? "instance" : "template"

  const [mode, setMode] = useState(initialMode)
  const [draftValue, setDraftValue] = useState(item.field[field] ?? "")
  const isEditingRef = useRef(false)

  useEffect(() => {
    if (!isEditingRef.current) {
      setDraftValue(item.field[field] ?? "")
    }
  }, [item.field.id, field, item.field[field]])

  const handleChangeDataSource = value => {
    setMode(value)
    const needsExpertMode = (value === "instance") && item.field[field].length !== 0 && !/^{{[^}]*}}$/.test(item.field[field])

    if (!needsExpertMode) {
      const newValue = (value === "template") ? dataSelector({ objectData: item.field, path: field, variables }) : findPathByValue(variables, item.field[field])
      const newItem = { ...item.field, [field]: newValue }

      if (field === "body" && !getCatalogPathFromReference(item.field[field])) {
        const catalogPath = getCatalogPathFromReference(newValue)
        if (catalogPath && catalogMap[catalogPath]) {
          applyFormattingPresetFromCatalog(newItem, catalogMap[catalogPath])
        }
      }

      setFields(prev => prev.map(i => i.field.id === newItem.id ? { ...i, field: newItem } : i))
    }
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <Typography sx={{ color: "secondary.main", marginLeft: 2, marginTop: 2 }}>{field_lookup[field]}</Typography>

      <CardContent>
      <RadioGroup
        row
        aria-labelledby="data_type_label"
        value={mode}
        onChange={e => handleChangeDataSource(e.target.value)}
        className='mb-5'
      >
        <FormControlLabel value="template" control={<Radio disabled={item.readonly?.[field]} />} label="Fixed text" />
        <FormControlLabel value="instance" control={<Radio disabled={item.readonly?.[field]} />} label="Use a variable" />
      </RadioGroup>

      {mode === "template" && <DataSourceTemplate item={item} field={field} draftValue={draftValue} setDraftValue={setDraftValue} isEditingRef={isEditingRef} />}
      {mode === "instance" && <DataSourceInstance item={item} field={field} />}
    </CardContent>

  </Card>
  )
}

const DataSourceTemplate = ({ item, field, draftValue, setDraftValue, isEditingRef }) => {

  const { setFields, path } = useContext(EditorContext)

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.field.id !== item.field.id) return f
      return { ...f, field: { ...f.field, [field]: value } }
    }))
  }

  return (
    <TextField label={field_lookup[field]}
      value={draftValue}
      slotProps={{ inputLabel: { shrink: true } }}
      type="text"
      disabled={item.readonly[field]}
      onFocus={() => { isEditingRef.current = true }}
      onBlur={() => {
        isEditingRef.current = false
        setDraftValue(item.field[field] ?? "")
      }}
      onChange={e => {
        const nextValue = e.target.value
        setDraftValue(nextValue)
        updateValue(nextValue)
      }}
      fullWidth
      multiline={path === "backFields"}
      maxRows={5}
    />
  )
}


const DataSourceInstance = ({ item, field }) => {
  return <SimpleInstanceSelector active item={item} field={field} />
}

const SimpleInstanceSelector = ({ active, item, field }) => {

  const { setFields, sourcePaths, resolveValue, variables, setVariables, dataSelector, path, catalogMap, validationErrors, setValidationErrors, isManagedPassTemplate } = useContext(EditorContext)

  const resolvedValue = useMemo(() => resolveValue(item.field, field), [resolveValue, item.field, field, variables])
  const variablesPath = item.field[field]?.replace(/[{}]/g, "")
  const variableValue = variablesPath ? _.get(variables, variablesPath) : ""
  const varSubType = catalogMap[variablesPath]?.var_type
  const catalogEntry = catalogMap[variablesPath]
  const isTextareaHint = catalogEntry?.var_ui_hint === "textarea"
  const instanceValueReadonly = item.readonly[field]
    || (catalogEntry?.editable !== undefined && Number(catalogEntry?.editable) === 0)
  const validationKey = `${item.field.id}:${field}`
  const [draftValue, setDraftValue] = useState(variableValue ?? "")

  useEffect(() => {
    setDraftValue(variableValue ?? "")
  }, [variableValue, item.field.id, field])

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.field.id !== item.field.id) return f

      const nextField = { ...f.field, [field]: value }
      const hadInstanceBinding = Boolean(getCatalogPathFromReference(f.field[field]))
      const nextInstancePath = getCatalogPathFromReference(value)

      if (
        field === "body" &&
        !hadInstanceBinding &&
        nextInstancePath &&
        catalogMap[nextInstancePath]
      ) {
        applyFormattingPresetFromCatalog(nextField, catalogMap[nextInstancePath])
      }

      return { ...f, field: nextField }
    }))
  }

  const updateResolvedValue = value => {
    if (instanceValueReadonly) return

    const normalisedValue = varSubType === "choice"
      ? value?.toString().trim().toUpperCase()
      : value

    setDraftValue(normalisedValue)

    const validation = validateValueInput({ input: normalisedValue, varSubType })
    setValidationErrors(prev => ({
      ...prev,
      [validationKey]: validation.valid ? "" : validation.helperText
    }))

    if (!validation.valid) return

    const variablesCopy = _.cloneDeep(variables)
    _.set(variablesCopy, variablesPath, normalisedValue)
    setVariables(variablesCopy)
  }

  const formattedValue = useMemo(() => getFormattedValue({ item:item.field, resolvedValue }), [resolvedValue, item.field.dateStyle, item.field.timeStyle, item.field.numberStyle, item.field.currencyCode])

  if (active) return (
    <>
      <VariablesSelector updateValue={updateValue} sourcePaths={sourcePaths} value={item.field[field]} dataSelector={dataSelector} variables={variables} readonly={item.readonly[field]}/>

      <TextField
        label={field_lookup[field]}
        value={draftValue}
        slotProps={{ inputLabel: { shrink: true } }}
        disabled={instanceValueReadonly}
        onChange={e => updateResolvedValue(e.target.value)}
        fullWidth
        sx={{ mb: 1 }}
        multiline={path === "backFields" || isTextareaHint}
        maxRows={5}
      />

      <SubtypeValueField label="Resolved value"
        value={resolvedValue ?? ""}
        varSubType={varSubType}
        disabled={true}
        fullWidth
        sx={{ mb: 1 }}
        multiline={path === "backFields" || isTextareaHint}
        maxRows={5}
        onChange={() => {}}
        slotProps={{ inputLabel: { shrink: true } }}
      />

      {["number", "money", "date", "time", "datetime"].includes(item.field.type) &&
        <TextField label="formatted value"
          value={formattedValue}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
          disabled={true}
          multiline={path === "backFields"}
          maxRows={5}
        />
      }
    </>
  )
}

const SubtypeValueField = ({ value, onChange, varSubType, helperText, error, multiline = false, ...props }) => {
  const dateInputRef = useRef(null)
  const dateTimeInputRef = useRef(null)

  const stringValue = value?.toString() ?? ""

  const endAdornment = (varSubType === "date" || varSubType === "datetime") ? (
    <InputAdornment position='end'>
      {varSubType === "date" ? (
        <>
          <IconButton edge='end' size='small' onClick={() => dateInputRef.current?.showPicker?.() ?? dateInputRef.current?.click()}>
            <EventOutlinedIcon fontSize='small' />
          </IconButton>
          <input
            ref={dateInputRef}
            type='date'
            value={toNativeDateValue(stringValue)}
            onChange={event => onChange(fromNativeDateValue(event.target.value))}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            tabIndex={-1}
          />
        </>
      ) : null}

      {varSubType === "datetime" ? (
        <>
          <IconButton edge='end' size='small' onClick={() => dateTimeInputRef.current?.showPicker?.() ?? dateTimeInputRef.current?.click()}>
            <ScheduleOutlinedIcon fontSize='small' />
          </IconButton>
          <input
            ref={dateTimeInputRef}
            type='datetime-local'
            step='1'
            value={toNativeDateTimeValue(stringValue)}
            onChange={event => onChange(fromNativeDateTimeValue(event.target.value))}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            tabIndex={-1}
          />
        </>
      ) : null}
    </InputAdornment>
  ) : null

  if (varSubType === "boolean") {
    return (
      <TextField
        {...props}
        select
        value={stringValue === "" ? "false" : stringValue}
        error={error}
        helperText={helperText}
        onChange={event => onChange(event.target.value)}
      >
        <MenuItem value="false">false</MenuItem>
        <MenuItem value="true">true</MenuItem>
      </TextField>
    )
  }

  if (varSubType === "colour") {
    const pickerColour = normaliseColourInput(stringValue) ?? "#ffffff"

    return (
      <div className='flex flex-col gap-2'>
        <TextField
          {...props}
          value={stringValue}
          error={error}
          helperText={helperText}
          onChange={event => onChange(event.target.value)}
        />
        <HexColorPicker color={pickerColour} onChange={onChange} />
      </div>
    )
  }

  if (varSubType === "choice") {
    return (
      <TextField
        {...props}
        select
        value={stringValue}
        error={error}
        helperText={helperText}
        onChange={event => onChange(event.target.value)}
      >
        <MenuItem value="">Select currency</MenuItem>
        {currencies.map(currency => (
          <MenuItem key={currency.code} value={currency.code}>
            {currency.code} - {currency.name}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  return (
    <TextField
      {...props}
      value={stringValue}
      type={varSubType === "email" ? "email" : varSubType === "phone" ? "tel" : "text"}
      multiline={varSubType === "text" ? multiline : false}
      minRows={varSubType === "text" && multiline ? 3 : undefined}
      error={error}
      helperText={helperText}
      onChange={event => {
        if (varSubType === "integer" && !isIntegerInput(event.target.value)) return
        if (["number", "money"].includes(varSubType) && !isDecimalInput(event.target.value)) return
        if (varSubType === "choice" && !/^[a-zA-Z]{0,3}$/.test(event.target.value)) return
        onChange(varSubType === "choice" ? event.target.value.toUpperCase() : event.target.value)
      }}
      inputProps={{
        inputMode: ["integer", "number", "money"].includes(varSubType) ? "decimal" : undefined,
        maxLength: varSubType === "choice" ? 3 : undefined,
        pattern: varSubType === "integer"
          ? "-?[0-9]*"
          : ["number", "money"].includes(varSubType)
            ? "-?[0-9]*[.]?[0-9]*"
            : varSubType === "choice"
              ? "[A-Za-z]{3}"
            : undefined
      }}
      InputProps={{
        endAdornment
      }}
      sx={{
        "& .MuiInputBase-input::placeholder, & .MuiInputBase-inputMultiline::placeholder": {
          color: "text.disabled",
          opacity: 1
        },
        ...(props.sx ?? {})
      }}
    />
  )
}

const FieldFormatting = ({ item }) => {

  const { setFields, path } = useContext(EditorContext)

  const updateValue = value => {
    setFields(prev => {

      const updatedFields = prev.map(f => {

        if (f.field.id === item.field.id) {
          const nextField = { ...f.field }
          applyFormattingDefaults(nextField, value)
          return { ...f, field: nextField }
        }

        return f
      })

      return updatedFields

    })
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      padding: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <Typography sx={{ color: "secondary.main", margin: 1 }}>Formatting</Typography>

      <RadioGroup
        row
        aria-labelledby="field_formatting_label"
        value={item.field.type}
        onChange={e => updateValue(e.target.value)}
      >
        <FormControlLabel value="text" control={<Radio />} label="None (Text)" disabled={item.readonly.body}/>
        <FormControlLabel value="number" control={<Radio />} label="Number"  disabled={item.readonly.body}/>
        <FormControlLabel value="date" control={<Radio />} label="Date"  disabled={item.readonly.body}/>
        <FormControlLabel value="time" control={<Radio />} label="Time"  disabled={item.readonly.body}/>
        <FormControlLabel value="datetime" control={<Radio />} label="Date and Time"  disabled={item.readonly.body}/>
        <FormControlLabel value="money" control={<Radio />} label="Money"  disabled={item.readonly.body}/>

        {path === "backFields" &&
          <>
            <FormControlLabel value="www" control={<Radio />} label="Website"  disabled={item.readonly.body}/>
            <FormControlLabel value="email" control={<Radio />} label="Email"  disabled={item.readonly.body}/>
            <FormControlLabel value="phone" control={<Radio />} label="Phone"  disabled={item.readonly.body}/>
            <FormControlLabel value="location" control={<Radio />} label="Map Location"  disabled={item.readonly.body}/>
          </>
        }
      </RadioGroup>
    </Card>)
}


const FieldFormattingSubTypeWrapper = ({ item }) => {

  if (["number", "date", "time", "datetime", "money"].includes(item.field.type)) return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      padding: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <FieldFormattingSubType item={item} />
    </Card>
  )
}

const FieldFormattingSubType = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateValue = (field, value) => {
    setFields(prev => prev.map(f => {
      if (f.field.id === item.field.id) f.field[field] = value
      return f
    }))
  }

  switch (item.field.type) {
    case "number": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Number format</Typography>
          <RadioGroup
            value={item.field.numberStyle}
            onChange={e => updateValue("numberStyle", e.target.value)}
          >
            <FormControlLabel value="PKNumberStyleDecimal" control={<Radio />} label="Decimal" />
            <FormControlLabel value="PKNumberStylePercent" control={<Radio />} label="Percentage" />
            <FormControlLabel value="PKNumberStyleScientific" control={<Radio />} label="Scientific" />
            <FormControlLabel value="PKNumberStyleSpellOut" control={<Radio />} label="Spell Out" />
          </RadioGroup>
        </>
      )
    }

    case "date": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Date format</Typography>
          <RadioGroup
            value={item.field.dateStyle}
            onChange={e => updateValue("dateStyle", e.target.value)}
          >
            <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide date)" />
            <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (DD/MM/YY)" />
            <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (DD MMM YYYY)" />
            <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (DD MMMM YYYY)" />
            <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (dddd DD MMMM YYYY)" />
          </RadioGroup>
        </>
      )
    }

    case "time": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Time format</Typography>
          <RadioGroup
            value={item.field.timeStyle}
            onChange={e => updateValue("timeStyle", e.target.value)}
          >
            <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide time)" />
            <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (HH:MM)" />
            <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (HH:MM:SS)" />
            <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (HH:MM:SS GMT)" />
            <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (HH:MM:SS Greenwich Mean Time)" />
          </RadioGroup>
        </>
      )
    }

    case "datetime": {
      return (
        <div className='flex flex-col gap-10 w-full'>
          <div>
            <Typography sx={{ color: "secondary.main", margin: 1 }}>Date format</Typography>
            <RadioGroup
              value={item.field.dateStyle}
              onChange={e => updateValue("dateStyle", e.target.value)}
            >
              <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide date)" />
              <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (DD/MM/YY)" />
              <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (DD MMM YYYY)" />
              <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (DD MMMM YYYY)" />
              <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (dddd DD MMMM YYYY)" />
            </RadioGroup>
          </div>

          <div>
            <Typography sx={{ color: "secondary.main", margin: 1 }}>Time format</Typography>
            <RadioGroup
              value={item.field.timeStyle}
              onChange={e => updateValue("timeStyle", e.target.value)}
            >
              <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide time)" />
              <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (HH:MM)" />
              <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (HH:MM:SS)" />
              <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (HH:MM:SS GMT)" />
              <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (HH:MM:SS Greenwich Mean Time)" />
            </RadioGroup>
          </div>
        </div>
      )
    }

    case "money": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Currency</Typography>
          <TextField
            select
            value={item.field.currencyCode ?? ""}
            onChange={e => updateValue("currencyCode", e.target.value)}
            fullWidth
          >
            <MenuItem value="">Select currency</MenuItem>
            {currencies.map(currency => (
              <MenuItem key={currency.code} value={currency.code}>
                {currency.code} - {currency.name}
              </MenuItem>
            ))}
          </TextField>
        </>
      )
    }
  }
}


const DateTimeOptions = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateValue = (field, value) => {
    setFields(prev => prev.map(f => {
      if (f.field.id === item.field.id) f.field[field] = value
      return f
    }))
  }

  useEffect(() => {
    if (["datetime", "date", "time"].includes(item.field.type)) {
      if (item.field.type === "time") {
        if (!!!item.field.ignoresTimeZone) updateValue("ignoresTimeZone", false)
      } else { // date / datetime
        if (!!!item.field.isRelative) updateValue("isRelative", false)
      }
    }
  }, [item])

  if (["datetime", "date", "time"].includes(item.field.type)) {

    return (
      <Card variant="outlined" sx={{
        minHeight: "min-content",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        marginBottom: 1,
        padding: 1,
        bgcolor: 'rgba(0,0,0,0.1)'
      }}>
        <Typography sx={{ color: "secondary.main", margin: 1 }}>Date/time options</Typography>

        <RadioGroup
          row
          aria-labelledby="field_formatting_label"
          value={item.field.ignoresTimeZone}
          onChange={e => updateValue("ignoresTimeZone", e.target.value)}
        >
          <FormControlLabel value={true} control={<Radio />} label="Ignore time zone" />
          <FormControlLabel value={false} control={<Radio />} label="Adjust to user's time zone" />
        </RadioGroup>

        {["datetime", "date"].includes(item.field.type) &&
          <RadioGroup
            row
            aria-labelledby="field_formatting_label"
            value={item.field.isRelative}
            onChange={e => updateValue("isRelative", e.target.value)}
          >
            <FormControlLabel value={false} control={<Radio />} label={`Display as absolute date`} />
            <FormControlLabel value={true} control={<Radio />} label={`Display as relative date`} />
          </RadioGroup>
        }
      </Card>
    )
  }
}


const ChangeMessage = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateValue = (field, value) => {
    setFields(prev => prev.map(f => {
      if (f.field.id === item.field.id) f.field[field] = value
      return f
    }))
  }

  const mode = item.field.body?.toString().match(/{{.*}}/) ? "instance" : "template"

  // No change message for links or template data (only instance data)
  if (mode === "instance" && ["text","number","money","date","time","datetime"].includes(item.field.type)) return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",  
      width: "100%",
      position: "relative",
      overflow: "hidden",
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <CardContent>
        <Typography sx={{ color: "secondary.main", marginBottom: 1 }}>Change Message</Typography>
        <TextField
          value={item.field.changeMessage}
          type="text"
          onChange={e => { updateValue("changeMessage",e.target.value) }}
          fullWidth
        />
        <p className='mt-2'><b>NOTE:</b> MAY contain <b>%@</b>. This will be substituted with the new value.</p>
        <p className='mt-2'>eg. "Date changed to %@" will appear as "Date changed to 12 May 2026" if the new value is "12 May 2026"</p>
        <p className='mt-2'>This is a polyfill implemented by Smartix to replicate Apple functionality.</p>

      </CardContent>
    </Card>
  )
}
